import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Grid,
  Button,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { getOrderDetail } from "../../../api/agency-orders/AgencyOrdersService";
import { formatOrderDate } from "../../../utils/dateOrders.utils";
import styles from "./index.module.css";
import HandleRequests from "../../Shared/handleRequests/HandleRequests";

const statusColors = {
  Pendiente: "warning",
  Aprobado: "info",
  Completado: "success",
  Rechazado: "error",
  Cancelado: "default",
};

const statusLabels = {
  Pendiente: "Pendiente",
  Aprobado: "Aprobado",
  Completado: "Completado",
  Rechazado: "Rechazado",
  Cancelado: "Cancelado",
};

export const DetallePedido = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const colors = useSelector((state) => state.local.uiConfig);

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (orderId) {
      loadOrderDetail();
    }
  }, [orderId]);

  const loadOrderDetail = async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await getOrderDetail(parseInt(orderId));
      console.log("Order detail response:", response);
      console.log("Agency data:", response?.agency);
      setOrder(response);
    } catch (err) {
      console.error("Error loading order detail:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !order) {
    return (
      <Grid container sx={{ p: 3, justifyContent: "center" }}>
        <CircularProgress />
      </Grid>
    );
  }

  if (error || !order) {
    return (
      <Grid container sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Error al cargar el pedido. Por favor, intente nuevamente.
        </Typography>
        <Button
          variant="outlined"
          onClick={() => navigate("/main/pedidos")}
          sx={{ mt: 2 }}
        >
          Volver al Listado
        </Button>
      </Grid>
    );
  }

  return (
    <Grid container sx={{ p: 3 }}>
      <HandleRequests loading={false} error={false}>
        <Grid container spacing={3}>
          {/* Header */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
              <IconButton onClick={() => navigate("/main/pedidos")}>
                <ArrowBackIcon
                  sx={{
                    fontSize: "1.8rem",
                    color: colors?.primary?.main || "primary.main",
                  }}
                />
              </IconButton>
              <ShoppingCartIcon
                sx={{
                  fontSize: "2rem",
                  color: colors?.primary?.main || "primary.main",
                }}
              />
              <Typography variant="h4" component="h1">
                Pedido #{order.nroOrder}
              </Typography>
            </Box>
          </Grid>

          {/* Información General */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Información General
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Estado
                  </Typography>
                  <Chip
                    label={statusLabels[order.status] || order.status}
                    color={statusColors[order.status] || "default"}
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Agencia
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {order?.agency
                      ? `${order.agency.number || ""}${order.agency.subNumber != null && order.agency.subNumber !== 0 ? ` - ${order.agency.subNumber}` : ""}${order.agency.name ? ` - ${order.agency.name}` : ""}`
                      : order?.agencyName || order?.agencyNumber
                      ? `${order.agencyNumber || ""}${order.agencyNumber && order.agencyName ? " - " : ""}${order.agencyName || ""}`
                      : "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Fecha de Solicitud
                  </Typography>
                  <Typography variant="body1">
                    {formatOrderDate(order.requestDate)}
                  </Typography>
                </Grid>
                {order.approvalDate && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Fecha de Aprobación
                    </Typography>
                    <Typography variant="body1">
                      {formatOrderDate(order.approvalDate)}
                    </Typography>
                  </Grid>
                )}
                {order.deliveryDate && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Fecha de Entrega
                    </Typography>
                    <Typography variant="body1">
                      {formatOrderDate(order.deliveryDate)}
                    </Typography>
                  </Grid>
                )}
                {order.approvedByUser && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Aprobado por
                    </Typography>
                    <Typography variant="body1">{order.approvedByUser}</Typography>
                  </Grid>
                )}
                {order.rejectionReason && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Motivo de Rechazo
                    </Typography>
                    <Typography variant="body1" color="error">
                      {order.rejectionReason}
                    </Typography>
                  </Grid>
                )}
                {order.observations && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Observaciones
                    </Typography>
                    <Typography variant="body1">{order.observations}</Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>

          {/* Detalles del Pedido */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Detalles del Pedido
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Juego</TableCell>
                      <TableCell>Sorteo</TableCell>
                      <TableCell align="right">Cant. Solicitada</TableCell>
                      <TableCell align="right">Cant. Aprobada</TableCell>
                      <TableCell>Observaciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order.details && order.details.length > 0 ? (
                      order.details.map((detail, index) => (
                        <TableRow key={detail.id || index}>
                          <TableCell>{detail.gameName || detail.game?.name}</TableCell>
                          <TableCell>
                            #{detail.raffleNumber || detail.raffle?.number}
                            {detail.raffle?.serie && ` - ${detail.raffle.serie}`}
                          </TableCell>
                          <TableCell align="right">
                            {detail.quantityRequested?.toLocaleString() || "-"}
                          </TableCell>
                          <TableCell align="right">
                            {detail.quantityApproved
                              ? detail.quantityApproved.toLocaleString()
                              : "-"}
                          </TableCell>
                          <TableCell>
                            {detail.observations || "-"}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No hay detalles para mostrar
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Información del Remito */}
          {order.deliveryNoteId && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3, backgroundColor: "success.light" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                  <ReceiptIcon sx={{ color: "success.main" }} />
                  <Typography variant="h6">Remito Generado</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Número de Remito
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      #{order.deliveryNoteId}
                    </Typography>
                  </Grid>
                  {order.deliveryDate && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        Fecha de Entrega
                      </Typography>
                      <Typography variant="body1">
                        {formatOrderDate(order.deliveryDate)}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>
          )}

          {/* Botón Volver */}
          <Grid item xs={12}>
            <Button
              variant="outlined"
              onClick={() => navigate("/main/pedidos")}
            >
              Volver al Listado
            </Button>
          </Grid>
        </Grid>
      </HandleRequests>
    </Grid>
  );
};



